/**
 * admin の PMTiles 生成パネル用の地方メタ (functions/src/admin/jpRegion.ts のミラー)。
 *
 * 地方別 OMT pmtiles は県単位クリップで重なりが無いため、県境をまたぐ地区 (箱根=神奈川+静岡)
 * は単一地方では全域をカバーできない。本ファイルは「地区 bbox に重なる地方」を既定選択にする
 * ための矩形・ラベルを持つ。実際のソース解決・合成は callable 側 (jpRegion.ts) が行う。
 * REGION_BBOXES の値・順序は functions 側と一致させること (離島を含む余裕を持った矩形)。
 */

export interface JpRegionBBox {
  west: number;
  south: number;
  east: number;
  north: number;
}

export interface JpRegionOption {
  code: string;
  label: string;
  bbox: JpRegionBBox;
}

/** functions/src/admin/jpRegion.ts の REGION_BBOXES と同値・同順。 */
export const JP_REGION_OPTIONS: JpRegionOption[] = [
  {
    code: 'kyushu',
    label: '九州・沖縄',
    bbox: { west: 122.5, south: 24.0, east: 132.5, north: 34.0 },
  },
  { code: 'shikoku', label: '四国', bbox: { west: 132.0, south: 32.5, east: 135.0, north: 34.5 } },
  { code: 'chugoku', label: '中国', bbox: { west: 130.7, south: 33.5, east: 134.7, north: 36.0 } },
  { code: 'kansai', label: '関西', bbox: { west: 134.0, south: 33.0, east: 137.0, north: 36.0 } },
  { code: 'chubu', label: '中部', bbox: { west: 135.0, south: 33.5, east: 139.5, north: 38.0 } },
  { code: 'kanto', label: '関東', bbox: { west: 138.5, south: 34.7, east: 141.5, north: 37.5 } },
  { code: 'tohoku', label: '東北', bbox: { west: 139.0, south: 36.5, east: 142.5, north: 41.5 } },
  {
    code: 'hokkaido',
    label: '北海道',
    bbox: { west: 139.5, south: 41.0, east: 146.0, north: 46.0 },
  },
];

/** functions の bboxFromCircle と同じ式で中心+半径から外接矩形を求める。 */
export function bboxFromCircle(lat: number, lng: number, radiusM: number): JpRegionBBox {
  const EARTH_R_KM = 6371;
  const KM_PER_DEG_LAT = (Math.PI * EARTH_R_KM) / 180;
  const radiusKm = radiusM / 1000;
  const dLat = radiusKm / KM_PER_DEG_LAT;
  const cosLat = Math.cos((lat * Math.PI) / 180);
  const dLng = radiusKm / (KM_PER_DEG_LAT * Math.max(cosLat, 1e-6));
  return { west: lng - dLng, south: lat - dLat, east: lng + dLng, north: lat + dLat };
}

/** bbox に矩形が重なる地方コードを宣言順で返す (既定選択用)。 */
export function regionsOverlappingBBox(bbox: JpRegionBBox): string[] {
  return JP_REGION_OPTIONS.filter((r) => {
    const d =
      r.bbox.east < bbox.west ||
      r.bbox.west > bbox.east ||
      r.bbox.north < bbox.south ||
      r.bbox.south > bbox.north;
    return !d;
  }).map((r) => r.code);
}
